import { useCallback, useEffect, useRef } from "react"
import { usePreferences } from "hooks/use-preferences"
import { useAppDispatch, useAppSelector } from "hooks/redux"
import { useDashboardId } from "../context/dashboard-id"
import { coreSlice, DEFAULT_CORE_STATE, CORE_PREFERENCE_FIELDS } from "../store/core-slice"
import { useDashboardPreferencesValidation } from "./preference-validation/use-dashboard-preferences-validation"

const PREFERENCE_SUBTYPE = "dashboard"
const PREFERENCE_FIELDS = CORE_PREFERENCE_FIELDS

/**
 * Syncs the core slice with user preferences via usePreferences.
 * Returns true once preferences have been loaded.
 */
export function useSyncCorePreferences(): boolean {
  const dashboardId = useDashboardId()
  const dispatch = useAppDispatch()
  const modelId = useAppSelector((s) => s.dashboard.model[dashboardId]?.modelId ?? null)
  const state = useAppSelector((s) => s.dashboard.core[dashboardId] ?? DEFAULT_CORE_STATE)
  const setPreferenceValidationOptions = useDashboardPreferencesValidation(modelId)

  const initialDashboardIdRef = useRef(dashboardId)
  useEffect(() => {
    if (process.env.NODE_ENV !== "production" && dashboardId !== initialDashboardIdRef.current) {
      throw new Error(
        `useSyncCorePreferences: dashboardId changed from "${initialDashboardIdRef.current}" to "${dashboardId}". `
        + "dashboardId must be stable for the component lifetime. "
        + "Unmount and remount DashboardLayout when navigating between dashboards, "
        + "or implement resetKey support in usePreferences.",
      )
    }
  }, [dashboardId])

  const setPreferencesAction = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (prefs: Record<string, any>) => coreSlice.actions.setPreferences({ dashboardId, ...prefs }),
    [dashboardId],
  )

  const isLoaded = usePreferences(
    PREFERENCE_SUBTYPE,
    PREFERENCE_FIELDS,
    state,
    dispatch,
    setPreferencesAction,
    { setPreferenceValidationOptions },
  )

  // When the user has no saved preferences, usePreferences sets isLoaded=true without
  // dispatching setPreferencesAction (because preferencesToApply is {}). This leaves
  // s.dashboard.core[dashboardId] undefined → isPreferencesLoaded stays false forever,
  // causing a permanent loading spinner for new users.
  useEffect(() => {
    if (isLoaded && !state.isPreferencesLoaded) {
      dispatch(coreSlice.actions.setPreferences({ dashboardId }))
    }
  }, [isLoaded, state.isPreferencesLoaded, dashboardId, dispatch])

  return isLoaded
}
